import Cookies from 'universal-cookie';

interface CookiePreferredLanguageData {
  storeId: string;
  countryCode: string;
  languageCode: string;
}

interface CookieSetOptions {
  path?: string;
  expires?: Date;
  maxAge?: number;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: boolean | 'none' | 'lax' | 'strict';
  partitioned?: boolean;
}

interface ICookieOptionsProvider {
  getOptions(key: string): CookieSetOptions;
}

export interface IPreferredStoreProvider {
  getStoreId(): string;

  getCountryCode(): string;

  getLanguageCode(): string;

  setStoreId(id: string): void;

  setCountryCode(id: string): void;

  setLanguageCode(code: string): void;
}

export class CookiePreferredStoreProvider implements IPreferredStoreProvider {
  private preferredLanguageKey = 'flokk-preferred-store-v1';

  private storage: CookieBasedStorage;

  constructor(
    cookies = new Cookies(),
    private cookieOptionsProvider?: ICookieOptionsProvider,
  ) {
    this.storage = new CookieBasedStorage(cookies, 365 * 86400, this.cookieOptionsProvider);
  }

  getStoreId(): string {
    return this.getPreferredLanguageCookieData().storeId;
  }

  getCountryCode(): string {
    return this.getPreferredLanguageCookieData().countryCode;
  }

  getLanguageCode(): string {
    return this.getPreferredLanguageCookieData().languageCode;
  }

  setStoreId(id: string): void {
    this.setPreferredLanguageCookieData({
      storeId: id,
    });
  }

  setCountryCode(code: string): void {
    this.setPreferredLanguageCookieData({
      countryCode: code,
    });
  }

  setLanguageCode(code: string): void {
    this.setPreferredLanguageCookieData({
      languageCode: code,
    });
  }

  private getPreferredLanguageCookieData() {
    const languagePreferredData = this.storage.get<CookiePreferredLanguageData>(this.preferredLanguageKey) || {
      languageCode: null,
      countryCode: null,
      storeId: null,
    };

    return languagePreferredData;
  }

  private setPreferredLanguageCookieData(cookieData: Partial<CookiePreferredLanguageData>) {
    const current = this.getPreferredLanguageCookieData();

    this.storage.set<CookiePreferredLanguageData>(this.preferredLanguageKey, {
      ...current,
      ...cookieData,
    });
  }
}

interface ILocalStorage {
  get<T>(key: string): T;

  set<T>(key: string, value: T): void;
}

class CookieBasedStorage implements ILocalStorage {
  private cookies: Cookies;
  private maxAgeInSeconds: number; // TODO: can be refactored into ICookieLifetime time with different strategies
  private cookieOptionsProvider?: ICookieOptionsProvider;

  constructor(cookies: Cookies, maxAgeInSeconds: number, cookieOptionsProvider?: ICookieOptionsProvider) {
    this.cookies = cookies;
    this.maxAgeInSeconds = maxAgeInSeconds;
    this.cookieOptionsProvider = cookieOptionsProvider;
  }

  get<T>(key: string): T {
    return this.cookies.get<T>(key);
  }

  set<T>(key: string, value: T): void {
    const cookieOptions = this.cookieOptionsProvider?.getOptions(key) ?? {};
    this.cookies.set(key, value, { maxAge: this.maxAgeInSeconds, path: '/', ...cookieOptions });
  }
}
